import { ReactNode, useEffect, useMemo, useState } from "react";
import IdleTimerContainer from "./IdleTimerContainer";
import LidAlert from "./lidAlert";
import SubscriptionChange from "./subscriptions/subscriptionChange";
import styles from "./styles";
import "./styles.css";
import { Layout, Menu, Spin } from "antd";
import sicom_logo from "../../../../assets/images/mini_sicom_logo.png";
import s_logo from "../../../../assets/images/mini_s.png";
import DistributionPointSelector from "common/components/widgets/DistributionPoint/distributionPointSelector";
import { getApplicationVersion } from "api/generated";
import { useClient } from "hooks/useClient";
import { RouteComponentProps, withRouter } from "react-router-dom";
import HeaderBar from "./HeaderBar";
import { LoadingOutlined } from "@ant-design/icons";
import { getTokenData } from "security";
import { useDistributionPoints } from "hooks/distributionPoints/useDistributionPoints";
import { MenuItem } from "../menu/menuTypes";
import useStockNotifications from "hooks/stock/useStockNotifications";
import ValidateDailyReportSchema from "./validateDailyReportSchema";
import ValidateMonthReportSchema from "./validateMonthReportSchema";
import { buildMenuItems } from "./menu/menuUtils";
import { useIsMobile } from "hooks/useIsMobile";
import { ItemType } from "antd/es/menu/interface";

interface MainLayoutProps extends RouteComponentProps {
  children: ReactNode;
  menu: MenuItem[];
}

function MainLayout({ children, menu, location }: MainLayoutProps) {
  const { client } = useClient();
  const [collapsed, setCollapsed] = useState(false);
  const [appVersion, setAppVersion] = useState("desarrollo");
  const {
    isLoading,
    userDistributionPoints,
    onSelectDistributionPointId,
    distributionPoint,
  } = useDistributionPoints();
  const { isMobile } = useIsMobile();
  const [userData, setUserData] = useState({
    userEmail: "",
    userRole: "",
    inventoryControl: false,
  });
  useStockNotifications(userData.inventoryControl);

  useEffect(() => {
    // Fetch Application Version
    getApplicationVersion({ client }).then(({ data }) => {
      setAppVersion(data ?? "desarrollo");
    });

    // Gets User Details
    const data = getTokenData() as {
      userEmail: string;
      userRole: string;
      permissions: { inventoryControl: { edit: boolean } };
    };
    setUserData({
      userEmail: data.userEmail,
      userRole: data.userRole,
      inventoryControl: data.permissions.inventoryControl.edit,
    });
  }, []);

  function openSubMenu() {
    if (
      location.pathname === "/dispensaries" ||
      location.pathname === "/dispensarySale" ||
      location.pathname === "/hoseSale"
    ) {
      return ["/dispensaries"];
    } else if (
      location.pathname === "/entryVolumeMeasurer" ||
      location.pathname === "/receptions"
    ) {
      return ["/entryVolumeMeasurer"];
    }
    return [location.pathname];
  }

  const menuItems = useMemo(() => {
    const distributionPointSelector: ItemType | undefined =
      userDistributionPoints.length > 0
        ? {
            key: "distribution-point-selector",
            label: (
              <div style={{ paddingBottom: "36px" }}>
                <DistributionPointSelector
                  allowedDistributionPoints={userDistributionPoints}
                  selectedDistributionPoint={distributionPoint?.id || ""}
                  onChangeDistributionPoint={onSelectDistributionPointId}
                />
              </div>
            ),
            type: "group",
          }
        : undefined;

    return buildMenuItems(menu, collapsed, distributionPointSelector);
  }, [menu, collapsed, userDistributionPoints, distributionPoint]);

  return (
    <div>
      <IdleTimerContainer />
      <LidAlert />
      <SubscriptionChange />
      <ValidateDailyReportSchema />
      <ValidateMonthReportSchema />
      <Layout style={styles.layout}>
        <Layout.Sider
          breakpoint="xl"
          width="315"
          style={{ overflow: "hidden" }}
          collapsible
          collapsed={collapsed}
          onCollapse={() => {
            setCollapsed(!collapsed);
          }}
        >
          <div className="logo">
            <img src={!collapsed ? sicom_logo : s_logo} alt="Sicom" />
          </div>

          <Menu
            theme="dark"
            inlineCollapsed={collapsed}
            items={menuItems}
            defaultSelectedKeys={[location.pathname]}
            defaultOpenKeys={openSubMenu()}
            mode="vertical"
            style={{
              height: `calc(100vh - 250px)`,
              overflowY: "auto",
            }}
          />
          <div className="appVersion" />
          <div className="appVersion">
            <p>
              <b>
                {collapsed ? "v-" : "Control Volumétrico v-"}
                {appVersion}
              </b>
            </p>
          </div>
          <div style={{ height: "48px" }} />
        </Layout.Sider>
        <Layout>
          <Layout.Header style={styles.header}>
            <HeaderBar
              pathname={location.pathname}
              userRole={userData.userRole}
              userEmail={userData.userEmail}
              menu={menu}
            />
          </Layout.Header>
          <Layout.Content
            style={{
              ...styles.content,
              ...(isMobile ? { margin: 0, padding: 0 } : {}),
            }}
          >
            {isLoading && (
              <div className="justify-center">
                <Spin
                  indicator={<LoadingOutlined style={styles.spin} spin />}
                />
              </div>
            )}
            {children}
          </Layout.Content>
        </Layout>
      </Layout>
    </div>
  );
}

export default withRouter(MainLayout);
